import { useState } from 'react';
import type { Payment, PaymentStatus } from '@salonomy/types';
import { Button, Card, EmptyState, Field, Pill, Select, Spinner } from '@salonomy/ui';
import { usePayments } from '../../data/payments.js';
import { useSalon } from '../../data/salon.js';
import { useAuth } from '../../lib/auth.js';
import { money } from '../../lib/format.js';
import { longDate } from '../../lib/time.js';
import {
  PAYMENT_KIND_LABELS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_LABELS,
  isRefundable,
  paymentStatusTone,
} from './paymentLabels.js';
import { RefundModal } from './RefundModal.js';
import './payments.css';

const STATUS_FILTERS: Array<{ value: '' | PaymentStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'partially_refunded', label: 'Partly refunded' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'failed', label: 'Failed' },
  { value: 'processing', label: 'Processing' },
];

/**
 * WS6 — the takings LEDGER. A paginated, filterable table of payments straight from `usePayments`. MONEY
 * HONESTY: every column (amount, refunded) is read off the Payment DTO; nothing is recomputed. The
 * Refund action is gated to managers and only enabled when the payment's status is refundable (succeeded
 * / partially_refunded) — a non-manager or a non-refundable row never fires a guaranteed-4xx.
 *
 * A DEPOSIT row's Refund action refunds the deposit (a normal refund). Forfeiting a deposit on a no-show
 * is the SEPARATE no-show-fee path (see PaymentsPage), so the labels keep the two from being confused.
 */
export function PaymentsLedger() {
  const { isManager } = useAuth();
  const { data: salon } = useSalon();
  const tz = salon?.timezone ?? 'Europe/London';

  const [status, setStatus] = useState<'' | PaymentStatus>('');
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [refundFor, setRefundFor] = useState<Payment | null>(null);

  const { data, isLoading, isError, isFetching } = usePayments({
    status: status || undefined,
    offset,
  });

  const rows = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const limit = data?.pagination.limit ?? 50;

  return (
    <Card>
      <div className="payments-head">
        <div>
          <div className="section-title" style={{ marginBottom: 2 }}>
            Takings ledger
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            Every payment, deposit, no-show fee and refund. Amounts are the payments server&rsquo;s.
          </div>
        </div>
        <div style={{ minWidth: 180 }}>
          <Field label="Status">
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as '' | PaymentStatus);
                setOffset(0);
                setExpandedId(null);
              }}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading the ledger…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load the takings ledger. It reads live from your payments — try again.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No payments here yet"
          description="Payments taken at the till, deposits and no-show fees land in this ledger."
        />
      ) : (
        <div className="payments-table" role="table">
          <div className="payments-row payments-row--head" role="row">
            <span>Date</span>
            <span>Reference</span>
            <span>Kind</span>
            <span>Method</span>
            <span>Status</span>
            <span className="payments-num">Amount</span>
            <span className="payments-num">Refunded</span>
            <span />
          </div>
          {rows.map((p) => (
            <LedgerRow
              key={p.id}
              payment={p}
              tz={tz}
              expanded={expandedId === p.id}
              isManager={isManager}
              onToggle={() => setExpandedId((cur) => (cur === p.id ? null : p.id))}
              onRefund={() => setRefundFor(p)}
            />
          ))}
        </div>
      )}

      {total > limit ? (
        <div className="payments-pager">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || isFetching}
            onClick={() => setOffset((o) => Math.max(0, o - limit))}
          >
            ← Newer
          </Button>
          <span className="muted" style={{ fontSize: 12.5 }}>
            {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + limit >= total || isFetching}
            onClick={() => setOffset((o) => o + limit)}
          >
            Older →
          </Button>
        </div>
      ) : null}

      {refundFor ? <RefundModal payment={refundFor} onClose={() => setRefundFor(null)} /> : null}
    </Card>
  );
}

function LedgerRow({
  payment,
  tz,
  expanded,
  isManager,
  onToggle,
  onRefund,
}: {
  payment: Payment;
  tz: string;
  expanded: boolean;
  isManager: boolean;
  onToggle: () => void;
  onRefund: () => void;
}) {
  const dateLabel = longDate(payment.createdAt.slice(0, 10), tz);
  const refundable = isRefundable(payment.status);
  const reference = payment.description ?? (payment.appointmentId ? 'Appointment' : '—');

  return (
    <>
      <button className="payments-row" role="row" onClick={onToggle}>
        <span className="muted" data-label="Date">
          {dateLabel}
        </span>
        <span className="payments-ref" data-label="Reference" title={reference}>
          {reference}
        </span>
        <span data-label="Kind">{PAYMENT_KIND_LABELS[payment.kind]}</span>
        <span className="muted" data-label="Method">
          {PAYMENT_METHOD_LABELS[payment.method]}
        </span>
        <span data-label="Status">
          <Pill tone={paymentStatusTone(payment.status)}>
            {PAYMENT_STATUS_LABELS[payment.status]}
          </Pill>
        </span>
        <span className="payments-num mono" data-label="Amount">
          {money(payment.amountPence)}
        </span>
        <span className="payments-num mono" data-label="Refunded">
          {payment.amountRefundedPence > 0 ? money(payment.amountRefundedPence) : '—'}
        </span>
        <span className="payments-chev">{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded ? (
        <div className="payments-detail" role="row">
          <div className="payments-detail__splits">
            <div className="slm-stat__label" style={{ marginBottom: 6 }}>
              Splits
            </div>
            {payment.splits.length === 0 ? (
              <div className="muted" style={{ fontSize: 12.5 }}>
                No splits recorded on this payment.
              </div>
            ) : (
              payment.splits.map((s) => (
                <div key={s.id} className="till__line" style={{ fontSize: 13 }}>
                  <span>{s.description ?? 'Split'}</span>
                  <span className="mono">{money(s.amountPence)}</span>
                </div>
              ))
            )}
            <div className="till__line muted" style={{ fontSize: 12.5, marginTop: 6 }}>
              <span>Platform fee</span>
              <span className="mono">{money(payment.platformFeePence)}</span>
            </div>
          </div>

          <div className="payments-detail__actions">
            {refundable ? (
              isManager ? (
                <Button variant="danger" size="sm" onClick={onRefund}>
                  {payment.kind === 'deposit' ? 'Refund deposit' : 'Refund'}
                </Button>
              ) : (
                <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                  Refunds are manager-only. Ask an owner or manager to refund this payment.
                </div>
              )
            ) : (
              <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                This payment isn&rsquo;t in a refundable state
                {payment.status === 'refunded' ? ' — it&rsquo;s already fully refunded.' : '.'}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
